import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { Gym } from '../types';
import { searchGymsByNameAllStates } from '../services/locationService';
import { Combobox, ComboboxOption } from '../src/components/ui/combobox';
import { DEFAULT_STATE_DISPLAY } from '../lib/stateRoutes';

interface HeaderSearchProps {
    /** Called when a gym is chosen from the autocomplete results. */
    onGymSelect: (gym: Gym) => void;
    /** Called after a successful selection — e.g. to close the mobile search overlay. */
    onAfterSelect?: () => void;
    /** Focus the input on mount (used by the mobile search overlay). */
    autoFocus?: boolean;
    /** Extra classes for the relative wrapper (positioning/width per context). */
    className?: string;
}

/**
 * Self-contained header gym-name search: dark-surface input + debounced Firestore
 * autocomplete dropdown with keyboard navigation. Extracted from Header so the SAME
 * field can be rendered both inline (md+) and inside the mobile search overlay.
 *
 * The combobox shell (open/keyboard/ARIA/dismiss) is the shared `Combobox` primitive;
 * this component owns only the dark styling + the debounced, request-sequenced data
 * layer that the primitive is deliberately agnostic about.
 */
const HeaderSearch: React.FC<HeaderSearchProps> = ({ onGymSelect, onAfterSelect, autoFocus = false, className = '' }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [searchResults, setSearchResults] = useState<Gym[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isDebouncing, setIsDebouncing] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const requestSeqRef = useRef(0);
    // 🪦 `routeStateSlug` / `routeStateSlugRef` LIVED HERE, and R-02's scoping with them.
    // R-02 scoped this bar to the current route's state, reading it through a ref so a
    // cross-state navigation with retained query text did not change performSearch's
    // identity (which re-fired the callable and re-opened a dismissed dropdown).
    //
    // ⚠️ ITEM 3 (operator-ruled 2026-08-17) REVERSES R-02 FOR THIS BAR: it now searches
    // EVERY launched state, so there is no route state to carry and the ref's whole reason
    // for existing went with it. `tsc` named all three lines unused.
    // ⚠️ THE STALE-CLOSURE HAZARD R-02's REF SOLVED IS GONE, NOT IGNORED — the search call
    // takes no state argument now, so navigation cannot change what it would query.
    // ⚠️ R-02 STILL GOVERNS `useDirectorySearch`, which is deliberately NOT universal: a
    // NSW directory listing Victorian gyms in its own results would read as a bug.

    // Debounced search — uses ONLY the Firestore display collection via Cloud Function.
    const performSearch = useCallback(async (query: string) => {
        const trimmed = query.trim();
        if (trimmed.length < 3) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }
        // Tag each request so a slower earlier response can't overwrite a newer one —
        // debounced type-ahead can otherwise resolve out of order and show stale results.
        const seq = ++requestSeqRef.current;
        setIsSearching(true);
        try {
            // ⭐ ITEM 3 (operator-ruled 2026-08-17): the GLOBAL bar searches EVERY launched
            // state, not the route's. The per-state fan-out and its ranking live in
            // services/locationService.ts + lib/gymSearchRanking.ts.
            const apiResults = await searchGymsByNameAllStates(trimmed);
            if (seq !== requestSeqRef.current) return;
            setSearchResults(apiResults);
        } catch (error) {
            if (seq !== requestSeqRef.current) return;
            console.error('Search error:', error);
            setSearchResults([]);
        } finally {
            if (seq === requestSeqRef.current) setIsSearching(false);
        }
    }, []);

    useEffect(() => {
        if (searchQuery.trim().length > 0) {
            setShowResults(true);
            setIsDebouncing(true);
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
                setIsDebouncing(false);
                performSearch(searchQuery);
            }, 400);
        } else {
            setSearchResults([]);
            setShowResults(false);
            setIsSearching(false);
            setIsDebouncing(false);
        }
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [searchQuery, performSearch]);

    useEffect(() => {
        if (autoFocus) inputRef.current?.focus();
    }, [autoFocus]);

    const handleGymClick = (gym: Gym) => {
        setSearchQuery('');
        setShowResults(false);
        onGymSelect(gym);
        onAfterSelect?.();
    };

    const clearSearch = () => {
        setSearchQuery('');
        setShowResults(false);
        setSearchResults([]);
        inputRef.current?.focus();
    };

    const options: ComboboxOption<Gym>[] = searchResults.map((gym, index) => ({
        id: gym.id || `gym-${index}`,
        value: gym,
    }));

    // Loading / no-results / min-chars messaging shown when there are no result rows.
    // (When stale results are still present mid-debounce the primitive shows them, matching prior behaviour.)
    const emptyState = (isSearching || isDebouncing) ? (
        <div className="px-4 py-6 text-center text-text-muted text-sm flex items-center justify-center gap-2 min-h-[120px]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Searching gyms...
        </div>
    ) : searchQuery.trim().length >= 3 ? (
        <div className="px-4 py-6 text-center text-text-muted text-sm">
            No gyms found matching &quot;{searchQuery}&quot;
        </div>
    ) : (
        <div className="px-4 py-6 text-center text-text-muted text-sm">
            Type at least 3 characters to search
        </div>
    );

    return (
        <Combobox<Gym>
            className={className}
            label="Gym name search results"
            options={options}
            onSelect={(gym) => handleGymClick(gym)}
            open={showResults}
            onOpenChange={setShowResults}
            wrap="clamp"
            selectFirstOnEnter
            resetKey={searchQuery}
            onEscape={clearSearch}
            panelClassName="left-0 right-0 bg-white rounded-xl shadow-xl border border-border-default overflow-hidden"
            listClassName="max-h-[314px] overflow-y-auto divide-y divide-border-default"
            emptyState={emptyState}
            renderOption={(opt, { active }) => {
                const gym = opt.value;
                return (
                    <div className={`px-4 py-3 cursor-pointer transition-colors ${active ? 'bg-brand-primary/10' : 'hover:bg-brand-primary/5'}`}>
                        <div className="text-text-primary font-medium text-sm">{gym.name}</div>
                        <div className="text-text-muted text-xs mt-0.5">{gym.suburb} {gym.state || DEFAULT_STATE_DISPLAY} {gym.postcode}</div>
                    </div>
                );
            }}
            renderInput={({ ref, getInputProps }) => (
                <>
                    <Search
                        aria-hidden="true"
                        className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-subtle pointer-events-none z-10"
                        strokeWidth={2}
                    />
                    {/* Header search input uses dark-on-dark colouring specific to this surface.
                        #2D333B and #444C56 are intentionally bespoke — no design-system tokens
                        exist for the dark-Header context because this is the only place they appear.
                        If a second consumer of these dark-input colours (#2D333B/#444C56)
                        appears, consider formalising these as surface-dark / border-dark tokens.
                        Font is text-base (16px) below lg so iOS Safari doesn't auto-zoom the page
                        on focus (matching the 16px hero location field); text-sm on desktop.
                        ⚠️ THE LABEL IS NOW STATIC, AND THE OLD NOTE HERE IS REVERSED. It read:
                        "the aria-label/placeholder are driven by the SAME hook the search call
                        uses, so the label cannot disagree with which state the search queries."
                        That coupling was right while the bar searched ONE state. Item 3 made it
                        search every launched state (operator-ruled 2026-08-17), so naming any
                        state would now be the disagreement the old note guarded against.
                        ⭐ "by name" is deliberate and was the operator's wording: this box
                        searches gym NAMES only, never suburbs, and the accessible label has said
                        "by name" since 2026-08-05 — so the visible text now finally matches what
                        screen-reader users already heard. State-neutral also means a third state
                        launch needs no edit here. */}
                    <input
                        {...getInputProps()}
                        ref={(node) => {
                            inputRef.current = node;
                            if (typeof ref === 'function') ref(node);
                            else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
                        }}
                        type="text"
                        aria-label="Search gyms by name"
                        placeholder="Search gyms by name..."
                        autoFocus={autoFocus}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => searchQuery.trim().length > 0 && setShowResults(true)}
                        className="w-full pl-10 pr-10 py-2.5 md:py-[9px] lg:py-[11px] bg-[#2D333B] border border-[#444C56] rounded-full text-white placeholder-text-subtle text-base lg:text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={clearSearch}
                            aria-label="Clear search"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-subtle hover:text-white transition-colors z-10"
                        >
                            <X size={16} />
                        </button>
                    )}

                    {/* Screen-reader announcement of search status / result count. */}
                    <span className="sr-only" role="status" aria-live="polite">
                        {(isSearching || isDebouncing) && searchResults.length === 0
                            ? 'Searching gyms'
                            : searchResults.length > 0
                                ? `${searchResults.length} gym${searchResults.length === 1 ? '' : 's'} found`
                                : searchQuery.trim().length >= 3
                                    ? 'No gyms found'
                                    : ''}
                    </span>
                </>
            )}
        />
    );
};

// Memoized: rendered twice under the React.memo'd Header, which re-renders on
// every menu/search/hover state change. Skips re-render while props are unchanged
// (effective when callers pass a stable onGymSelect reference).
export default React.memo(HeaderSearch);
